import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/layout/Navbar';
import PageHeader from '../components/layout/PageHeader';
import { Shield, ArrowRight, Users, Link2, Flag, MailCheck, Hourglass, Plus, PauseCircle, Search } from 'lucide-react';
import FindYourClub from '../components/findClub/FindYourClub';
import './MyClubsPage.css';

const ROLE_LABELS = {
  admin: 'Admin',
  team_manager: 'Team Manager',
  coach: 'Coach',
  player: 'Player',
  parent: 'Parent / Guardian',
  supporter: 'Supporter',
  sponsor: 'Sponsor',
};

/**
 * B-5 — the "no club yet" landing state (UI_TWEAKS_TRACKER, agreed 14 Jul;
 * reworked 27 Aug for self-serve, slice 2).
 *
 * A registered user with no memberships gets the paths forward:
 *   0. B-58 (Stuart, 12 Oct): Find your club — search verified clubs by name
 *      or town, tap through to the club's hub and Request to join there.
 *   1. Already on Marakin? Ask your club admin/coach for an invite link.
 *   2. Want Marakin for your club? → the self-serve creation wizard at
 *      /create-club (replaced the old 'club_request' support-ticket form;
 *      staff are emailed on every creation, which is the verification queue).
 */
const NoClubLanding = () => (
  <div className="no-club-landing">
    {/* Path 0 — B-58: find the club and ask to join */}
    <div className="no-club-card">
      <div className="no-club-card-icon"><Search size={22} /></div>
      <div className="no-club-card-body">
        <h3>Find your club</h3>
        <p>Search for your club, open it and ask to join. An admin at the club approves it.</p>
        <FindYourClub />
      </div>
    </div>

    {/* Path 1 — the invite link */}
    <div className="no-club-card">
      <div className="no-club-card-icon"><Link2 size={22} /></div>
      <div className="no-club-card-body">
        <h3>Already on Marakin at your club?</h3>
        <p>
          Clubs add members by <strong>invite link</strong> — ask your club admin or a
          coach to send you one. Opening the link connects this account to your club
          with the right role, and everything appears here.
        </p>
      </div>
    </div>

    {/* Path 2 — start your club, self-serve */}
    <div className="no-club-card">
      <div className="no-club-card-icon"><Flag size={22} /></div>
      <div className="no-club-card-body">
        <h3>Want Marakin for your club?</h3>
        <p>
          Set it up yourself in about two minutes — pick your sport, name your club,
          add your teams, and you're its admin. No forms, fees or fuss for your committee.
        </p>
        <Link to="/create-club" className="btn btn-primary">
          Start your club <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  </div>
);

/**
 * B-59 (9 Oct 2026) — "Confirm your email". A club invite is waiting for this
 * account's email, but invites are only applied once the email is proven
 * (the server emailed a 6-digit code at sign-up / sign-in). One field, one
 * button; on success the invite is applied and refreshUser() takes them in.
 */
const ConfirmEmailCard = ({ email, onConfirmed }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // QA (9 Oct): the server emails a code at sign-in / sign-up, but someone
  // arriving on a saved session (or after 15 minutes) has no live code.
  // Ask once on show; the server keeps a code that is still valid.
  // If that send is refused (daily limit), say so instead of "we've emailed".
  useEffect(() => {
    axios.post('/auth/email-code', { onlyIfNoneLive: true })
      .catch(err => { if (err.response?.status === 429 && !err.response?.data?.retryAfter) setError(err.response.data.error); });
  }, []);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setInfo('');
    try {
      await axios.post('/auth/email-code/verify', { code });
      await onConfirmed();
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setError(''); setInfo('');
    try {
      await axios.post('/auth/email-code');
      setInfo('New code sent. Check your inbox.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send a code. Please try again.');
    }
  };

  return (
    <div className="no-club-card confirm-email-card">
      <div className="no-club-card-icon"><MailCheck size={22} /></div>
      <div className="no-club-card-body">
        <h3>Confirm your email to join</h3>
        <p>
          A club has invited <strong>{email}</strong>. We've emailed a 6-digit code
          there. Enter it below and you're in.
        </p>
        <form onSubmit={verify} className="confirm-email-form">
          <input
            className="input confirm-email-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="123456"
            aria-label="6-digit code"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          <button type="submit" className="btn btn-primary" disabled={busy || code.length !== 6}>
            {busy ? 'Checking…' : 'Join'}
          </button>
        </form>
        {error && <p className="form-error" style={{ marginTop: 8 }}>{error}</p>}
        {info && <p className="confirm-email-info">{info}</p>}
        <p className="confirm-email-help">
          No email? Check spam, or{' '}
          <button type="button" className="link-button" onClick={resend}>send a new code</button>.
          The link in your invite email works too.
        </p>
      </div>
    </div>
  );
};

/**
 * WT-4 (16 Oct walk-through; B-58 slice 3's waiting card): join requests
 * waiting for a club admin, from GET /me/join-requests (/auth/me lists
 * approved clubs only, so these used to be invisible). Withdraw = DELETE
 * /clubs/:slug/join, own waiting request only, after a confirm. Same query
 * key as the club hub's banner, so both stay in step.
 */
const WaitingRequests = ({ requests }) => {
  const qc = useQueryClient();
  const [errors, setErrors] = useState({}); // club_slug → server message
  const withdraw = useMutation({
    mutationFn: (slug) => axios.delete(`/clubs/${slug}/join`),
    onMutate: (slug) => setErrors(e => ({ ...e, [slug]: null })),
    onSuccess: (res, slug) => {
      // Drop it from the cache at once, so Withdraw can't be pressed twice.
      qc.setQueryData(['join-requests'], old => (old || []).filter(r => r.club_slug !== slug));
      qc.invalidateQueries({ queryKey: ['join-requests'] });
    },
    onError: (err, slug) => {
      setErrors(e => ({ ...e, [slug]: err?.response?.data?.error || 'We couldn\'t withdraw your request. Please try again.' }));
      // Not waiting any more (an admin answered meanwhile): refresh the list.
      if (err?.response?.status === 404) qc.invalidateQueries({ queryKey: ['join-requests'] });
    },
  });
  const ask = (r) => {
    if (!window.confirm(`Withdraw your request to join ${r.club_name}? You can ask again later.`)) return;
    withdraw.mutate(r.club_slug);
  };

  return (
    <section className="my-clubs-waiting" data-testid="my-clubs-waiting" aria-labelledby="my-clubs-waiting-title">
      <h2 id="my-clubs-waiting-title" className="my-clubs-waiting-title"><Hourglass size={17} /> Waiting for an admin</h2>
      <div className="my-clubs-grid">
        {requests.map(r => {
          const when = r.requested_at ? new Date(r.requested_at) : null;
          const on = when && !Number.isNaN(when.getTime())
            ? ` on ${when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : '';
          const as = ROLE_LABELS[r.role] ? ` as a ${ROLE_LABELS[r.role]}` : '';
          const busy = withdraw.isPending && withdraw.variables === r.club_slug;
          return (
            <div key={r.club_slug} className="club-choice-card club-waiting-card">
              <div className="club-choice-logo club-waiting-logo">
                {r.club_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="club-choice-info">
                <h3 className="club-choice-name">{r.club_name}</h3>
                {r.sport && <p className="club-choice-sport">{r.sport}</p>}
                <p className="club-waiting-when">You asked{on}{as}. We'll let you know when an admin approves it.</p>
                {errors[r.club_slug] && <p className="form-error" role="alert">{errors[r.club_slug]}</p>}
                <div className="club-waiting-actions">
                  <Link to={`/${r.club_slug}`} className="btn btn-secondary btn-sm">View club</Link>
                  <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => ask(r)}>
                    {busy ? 'Withdrawing…' : 'Withdraw request'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const MyClubsPage = () => {
  const { user, loading, impersonating, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const memberships = user?.memberships || [];
  // /wallet shortcut with several clubs and no card (WalletRedirect): offer the Wallets.
  const wantWallet = location.state?.next === 'wallet';
  // B-59: a club invite is waiting for this email, pending the code.
  const needsCode = !!user && !impersonating && user.emailVerified === false && user.invitesWaiting > 0;

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    // Auto-redirect single-club users unless in impersonation mode
    // B-38 item 4: not into a paused club (its page only says it's paused and
    // links back here), so they see it listed with its Paused label instead.
    if (memberships.length === 1 && !impersonating && !needsCode && !memberships[0].clubSuspended) {
      const m = memberships[0];
      navigate(m.role === 'sponsor' ? `/${m.clubSlug}/scan` : `/${m.clubSlug}`, { replace: true });
    }
  }, [user, loading, impersonating, needsCode]);

  // WT-4: waiting join requests. Not fetched when this page only redirects
  // (one club) or for staff accounts (no club rows of their own).
  const autoRedirect = memberships.length === 1 && !impersonating && !needsCode && !memberships[0].clubSuspended;
  const { data: waiting = [] } = useQuery({
    queryKey: ['join-requests'],
    queryFn: () => axios.get('/me/join-requests').then(r => (Array.isArray(r.data) ? r.data : [])),
    enabled: !!user && !loading && !user.isPlatformUser && !autoRedirect,
    staleTime: 30000,
  });

  if (loading || autoRedirect) {
    return (
      <div className="page-loading">
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  const displayName = impersonating
    ? `${impersonating.firstName} ${impersonating.lastName}`
    : user?.firstName;

  const noClubs = memberships.length === 0;

  return (
    <div className="my-clubs-page">
      <Navbar />

      <PageHeader
        icon={Users}
        title={noClubs && !impersonating ? `Welcome, ${displayName || 'there'}` : 'My Clubs'}
        subtitle={
          impersonating ? (
            <>Viewing as <strong>{displayName}</strong> — select a club to see their experience.</>
          ) : noClubs && needsCode ? (
            <>You've been invited. One quick step and you're in.</>
          ) : noClubs && waiting.length > 0 ? (
            <>Your request is with the club. We'll let you know when an admin approves it.</>
          ) : noClubs ? (
            <>Your account is ready — let's get you connected to a club.</>
          ) : wantWallet ? (
            <>You're in {memberships.length} clubs — which club's Wallet do you want?</>
          ) : (
            <>You're a member of {memberships.length} club{memberships.length !== 1 ? 's' : ''}. Where would you like to go?</>
          )
        }
      />

      <div className="my-clubs-inner">
        {needsCode && <ConfirmEmailCard email={user.email} onConfirmed={refreshUser} />}
        {/* WT-4: no club yet → the waiting request is the news, so it leads;
            otherwise it sits under the clubs they're already in. */}
        {noClubs && waiting.length > 0 && <WaitingRequests requests={waiting} />}
        {/* 29 Oct walkthrough (Stuart chose option A): Join and Start sit as
            two small cards ABOVE the club list, not after it. B-58 (Join →
            /find-club) and B-83 (Start → /create-club) rules unchanged: not
            in view-as, not for sponsor-only logins. */}
        {!noClubs && !impersonating && !memberships.every(m => m.role === 'sponsor') && (
          <div className="my-clubs-quick">
            <Link to="/find-club" className="my-clubs-quick-card is-join" data-testid="my-clubs-find">
              <Search size={20} aria-hidden="true" />
              <span className="my-clubs-quick-title">Join a club</span>
              <span className="my-clubs-quick-sub">Find yours and ask to join</span>
            </Link>
            <Link to="/create-club" className="my-clubs-quick-card is-start">
              <Plus size={20} aria-hidden="true" />
              <span className="my-clubs-quick-title">Start a club</span>
              <span className="my-clubs-quick-sub">Set one up in two minutes</span>
            </Link>
          </div>
        )}
        {noClubs ? (
          !needsCode && <NoClubLanding />
        ) : (
          <div className="my-clubs-grid">
            {memberships.map(m => {
              const dest = m.role === 'sponsor' ? `/${m.clubSlug}/scan` : wantWallet ? `/${m.clubSlug}/wallet` : `/${m.clubSlug}`;
              const accentColor = m.primaryColor || '#1E4A35';
              return (
                <Link key={m.clubSlug} to={dest} className="club-choice-card">
                  <div className="club-choice-logo" style={{ background: accentColor }}>
                    {m.clubName?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="club-choice-info">
                    <h2 className="club-choice-name">{m.clubName}</h2>
                    {m.sport && <p className="club-choice-sport">{m.sport}</p>}
                    {m.location && <p className="club-choice-location">📍 {m.location}</p>}
                    <div className="club-choice-badges">
                      <span
                        className="badge club-choice-role"
                        style={{ background: `${accentColor}18`, color: accentColor }}
                      >
                        <Shield size={10} /> {ROLE_LABELS[m.role] || m.role}
                      </span>
                      {m.clubSuspended && (
                        <span className="badge club-choice-role club-choice-paused" title="This club is paused. Contact Marakin.">
                          <PauseCircle size={10} /> Paused
                        </span>
                      )}
                    </div>
                  </div>
                  <ArrowRight size={20} className="club-choice-arrow" />
                </Link>
              );
            })}
          </div>
        )}
        {!noClubs && waiting.length > 0 && <WaitingRequests requests={waiting} />}
      </div>
    </div>
  );
};

export default MyClubsPage;
